/**
 * Pulls a status code / human-readable message out of whatever `@ts-rest/react-query` throws.
 * RN-safe (no DOM/Node built-ins), so both `apps/web` and `apps/mobile` use this instead of each
 * hand-maintaining an identical copy — it drifted before (this used to be two byte-for-byte
 * identical files) with zero platform-specific logic in either.
 *
 * A non-2xx response is thrown as `{ status, body, headers }` (see `apiFetcher` in
 * `@ts-rest/react-query`'s v5 hooks) rather than surfaced as `data` — `useMutation`'s `onError` and
 * a query's `error` both receive that shape. `body` matches `errorBodySchema` from this package on
 * every route either app calls, so `.body.message` is the right thing to show whenever it is
 * present; a network failure (offline, DNS) throws a plain `Error` instead, so that branch falls
 * back to `.message`.
 */
export function apiErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}

export function apiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (error && typeof error === 'object' && 'body' in error) {
    const body = (error as { body?: unknown }).body;
    if (body && typeof body === 'object' && 'message' in body) {
      const message = (body as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
  }
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}
